import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Switch,
  TextField,
  Typography,
  Box,
  Divider,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { generateAvery5160PDF } from '../utils/generateAveryPDF';
import { generateCSV } from '../utils/generateCSV';

/**
 * Dialog for exporting addresses to Avery 18294 label PDF
 * Dynamically detects categories from addresses and allows filtering
 */
function ExportDialog({ open, handleClose, addresses }) {
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [exportFormat, setExportFormat] = useState('csv'); // 'pdf' or 'csv'
  const [debugMode, setDebugMode] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const [verticalOffset, setVerticalOffset] = useState(0);
  const [includeReturnLabels, setIncludeReturnLabels] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Dynamically extract unique categories from addresses
  const categories = useMemo(() => {
    const categorySet = new Set();
    addresses.forEach(addr => {
      if (addr.category) {
        categorySet.add(addr.category);
      }
    });
    return Array.from(categorySet).sort();
  }, [addresses]);

  // Initialize with all categories selected
  useEffect(() => {
    if (categories.length > 0 && selectedCategories.size === 0) {
      setSelectedCategories(new Set(categories));
    }
  }, [categories]);

  // Filter addresses by selected categories
  const filteredAddresses = useMemo(() => {
    return addresses.filter(addr => {
      // Include addresses without category if any category is selected
      if (!addr.category) return selectedCategories.size > 0;
      return selectedCategories.has(addr.category);
    });
  }, [addresses, selectedCategories]);

  const handleCategoryToggle = (category) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      newSelected.add(category);
    }
    setSelectedCategories(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedCategories(new Set(categories));
  };

  const handleDeselectAll = () => {
    setSelectedCategories(new Set());
  };

  const handleGenerateExport = async () => {
    if (filteredAddresses.length === 0) {
      alert('Please select at least one category with addresses.');
      return;
    }

    if (exportFormat === 'csv') {
      const result = generateCSV(filteredAddresses);
      if (result.success) {
        handleClose();
      }
    } else {
      // Async PDF generation
      const result = await generateAvery5160PDF(filteredAddresses, {
        debugMode,
        horizontalOffset: parseFloat(horizontalOffset) || 0,
        verticalOffset: parseFloat(verticalOffset) || 0,
        includeReturnLabels,
        useCustomFont: true
      });

      if (result.success) {
        handleClose();
      }
    }
  };

  const handlePreview = async () => {
    if (filteredAddresses.length === 0) return;
    
    // Generate just a sample (first 60 or all if less) to be fast
    // Actually, generating all is fast enough usually.
    const result = await generateAvery5160PDF(filteredAddresses, {
      debugMode,
      horizontalOffset: parseFloat(horizontalOffset) || 0,
      verticalOffset: parseFloat(verticalOffset) || 0,
      includeReturnLabels,
      useCustomFont: true,
      returnBlob: true
    });

    if (result.success && result.blobUrl) {
      setPreviewUrl(result.blobUrl);
    }
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Export to Avery 18294 Labels</DialogTitle>
      
      <DialogContent>
        {/* Export Format Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Export Format</InputLabel>
          <Select
            value={exportFormat}
            label="Export Format"
            onChange={(e) => setExportFormat(e.target.value)}
          >
            <MenuItem value="csv">CSV (for Google Docs Mail Merge)</MenuItem>
            <MenuItem value="pdf">PDF (Direct Print)</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Category Selection */}
        <Typography variant="subtitle2" gutterBottom>
          Select Categories to Export
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
            Select All
          </Button>
          <Button size="small" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </Box>

        <FormGroup sx={{ mb: 3 }}>
          {categories.length === 0 ? (
            <Alert severity="info">No categories found in addresses</Alert>
          ) : (
            categories.map(category => (
              <FormControlLabel
                key={category}
                control={
                  <Checkbox
                    checked={selectedCategories.has(category)}
                    onChange={() => handleCategoryToggle(category)}
                  />
                }
                label={category}
              />
            ))
          )}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Preview Info */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {exportFormat === 'csv' ? (
            <>{filteredAddresses.length} address{filteredAddresses.length !== 1 ? 'es' : ''} will be exported to CSV</>
          ) : (
            <>{filteredAddresses.length * (includeReturnLabels ? 2 : 1)} label{filteredAddresses.length !== 1 ? 's' : ''} will be generated ({Math.ceil((filteredAddresses.length * (includeReturnLabels ? 2 : 1)) / 60)} page{Math.ceil((filteredAddresses.length * (includeReturnLabels ? 2 : 1)) / 60) !== 1 ? 's' : ''})</>
          )}
        </Alert>

        {/* Debug Mode - Only for PDF */}
        {exportFormat === 'pdf' && (
          <FormControlLabel
            control={
              <Switch
                checked={debugMode}
                onChange={(e) => setDebugMode(e.target.checked)}
              />
            }
            label="Debug Mode (colored boxes, grid, measurements)"
          />
        )}
        
        {exportFormat === 'pdf' && debugMode && (
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            <Typography variant="caption" component="div">
              <strong>Debug Mode Features:</strong><br/>
              • <span style={{color: 'red'}}>Red boxes</span> = Label boundaries<br/>
              • <span style={{color: 'blue'}}>Blue crosses</span> = Corner markers<br/>
              • <span style={{color: 'blue'}}>Blue #numbers</span> = Label position<br/>
              • Gray grid = 5mm spacing inside labels<br/>
              • Edge measurements = Distance from page edge in mm<br/>
              <br/>
              <strong>How to use:</strong> Print on regular paper, compare to actual label sheet, measure any offset, then enter in calibration fields below.
            </Typography>
          </Alert>
        )}

        {exportFormat === 'pdf' && <Divider sx={{ my: 2 }} />}

        {/* Printer Calibration - Only for PDF */}
        {exportFormat === 'pdf' && (
        <>
        <Typography variant="subtitle2" gutterBottom>
          Options
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={includeReturnLabels}
              onChange={(e) => setIncludeReturnLabels(e.target.checked)}
            />
          }
          label="Include Return Address Labels (Doubles count)"
        />
        
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Printer Calibration (optional)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Adjust if labels don't align perfectly with your printer
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Horizontal Offset (mm)"
            type="number"
            size="small"
            value={horizontalOffset}
            onChange={(e) => setHorizontalOffset(e.target.value)}
            inputProps={{ step: 0.5 }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Vertical Offset (mm)"
            type="number"
            size="small"
            value={verticalOffset}
            onChange={(e) => setVerticalOffset(e.target.value)}
            inputProps={{ step: 0.5 }}
            sx={{ flex: 1 }}
          />
        </Box>
        </>
        )}

        {/* PDF Preview Iframe */}
        {previewUrl && exportFormat === 'pdf' && (
          <Box sx={{ mt: 3, border: '1px solid #ccc', height: '600px' }}>
            <iframe 
              src={previewUrl} 
              width="100%" 
              height="100%" 
              title="Label Preview"
              style={{ border: 'none' }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleGenerateExport} 
          variant="contained" 
          disabled={filteredAddresses.length === 0}
        >
          {exportFormat === 'csv' ? 'Export CSV' : 'Download PDF'}
        </Button>
        {exportFormat === 'pdf' && (
          <Button 
            onClick={handlePreview} 
            variant="outlined" 
            sx={{ ml: 1 }}
            disabled={filteredAddresses.length === 0}
          >
            Preview
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ExportDialog;
